// All types/interfaces under the lesson/resource domain (excluding assignment / question / attempts)
import { SubjectCategory } from "@modules/subject-admin/service/subject.admin.constant.ts";
import { reflectKeys } from "@utils/key.reflactor.ts";
import { QuizTestMode } from "@modules/print-view/service/print.view.constant.ts";
import { QuestionOption, QuestionType } from "@/types/question.ts";

// TODO: There are too many variants of RESOURCE_TYPE and RESOURCE_STATUS,
//  hence not moved into lesson.ts

// RESOURCE_STATUS/RESOURCE_CONTEXT_TYPE are based off lesson API fetch
export const RESOURCE_STATUS = reflectKeys([
  "DRAFT",
  "PENDING_APPROVAL",
  "PENDING_UNPUBLISH",
  "APPROVED",
  "UNPUBLISHED",
  "REJECTED",
  "ARCHIVED",
]);
export type ResourceStatus = keyof typeof RESOURCE_STATUS;

const RESOURCE_PATH = {
  MY_DRIVE: "my-drive",
  MOE_LIBRARY: "moe-library",
  COMMUNITY_GALLERY: "community-gallery",
  ALP_TEMPLATE: "alp-template",
  ASSIGNMENT: "assignment",
  CLASS_GROUP: "class-group",
};
const RESOURCE_DOMAIN = {
  PERSONAL: "PERSONAL",
  MOE: "MOE",
  COMMUNITY: "COMMUNITY",
  ALP_TEMPLATE: "ALP_TEMPLATE",
  ASSIGNMENT: "ASSIGNMENT",
};
export type ResourceDomain = keyof typeof RESOURCE_DOMAIN;

export const RESOURCE_CONTEXT_TYPE = reflectKeys([
  ...Object.keys(RESOURCE_DOMAIN),
  "ASSIGNMENT",
  "QUESTION_ATTEMPT",
  "FORUM_TOPIC_RESOURCE",
]);
export type ResourceContextType = keyof typeof RESOURCE_CONTEXT_TYPE;

// TODO: SLS-11043 (DONE), to review the usage
// TODO: to be refactored with routes.ts?
export const RESOURCE_DOMAIN_PATH = {
  PERSONAL: RESOURCE_PATH.MY_DRIVE,
  MY_LIBRARY: RESOURCE_PATH.MY_DRIVE,
  MOE: RESOURCE_PATH.MOE_LIBRARY,
  MOE_LIBRARY: RESOURCE_PATH.MOE_LIBRARY,
  SELF_STUDY: RESOURCE_PATH.MOE_LIBRARY,
  COMMUNITY: RESOURCE_PATH.COMMUNITY_GALLERY,
  COMMUNITY_GALLERY: RESOURCE_PATH.COMMUNITY_GALLERY,
  ALP_TEMPLATE: RESOURCE_PATH.ALP_TEMPLATE,
  ASSIGNMENT: RESOURCE_PATH.ASSIGNMENT,
  CLASS_GROUP_RESOURCE: RESOURCE_PATH.CLASS_GROUP,
  PERSONAL_LESSON: RESOURCE_PATH.MY_DRIVE,
};
export const RESOURCE_DOMAIN_PATH_TYPE = {
  [RESOURCE_PATH.ASSIGNMENT]: RESOURCE_DOMAIN.ASSIGNMENT,
  [RESOURCE_PATH.CLASS_GROUP]: RESOURCE_DOMAIN.ASSIGNMENT,
  [RESOURCE_PATH.MY_DRIVE]: RESOURCE_DOMAIN.PERSONAL,
  [RESOURCE_PATH.COMMUNITY_GALLERY]: RESOURCE_DOMAIN.COMMUNITY,
  [RESOURCE_PATH.ALP_TEMPLATE]: RESOURCE_DOMAIN.ALP_TEMPLATE,
  [RESOURCE_PATH.MOE_LIBRARY]: RESOURCE_DOMAIN.MOE,
};

export const COMPONENT_MODE = reflectKeys(["ATTEMPT", "RESULT"]);
export type ComponentMode = keyof typeof COMPONENT_MODE;

export const COMPONENT_TYPE = {
  ACCORDION: "CONTAINER_ACCORDION",
  TWO_COLUMN: "CONTAINER_TWO_COLUMN",
  THREE_COLUMN: "CONTAINER_THREE_COLUMN",
  QUESTION: "COMPONENT_QUESTION",
  TEXT: "COMPONENT_RICHTEXT",
  YOUTUBE: "COMPONENT_YOUTUBE",
  MO: "COMPONENT_MEDIAOBJECT",
  POLL: "COMPONENT_POLL",
  TOPIC: "COMPONENT_TOPIC",
};
export type ComponentType = keyof typeof COMPONENT_TYPE;

export const LAC_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
  "CONTAINER_ACCORDION_ITEM",
  "COMPONENT_QUESTION",
  "COMPONENT_RICHTEXT",
  "COMPONENT_POLL",
  "COMPONENT_TOPIC",
  "ACTIVITY_QUIZ",
]);
export type LacType = keyof typeof LAC_TYPE;

export const LA_CONTAINER_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
  "CONTAINER_ACCORDION_ITEM",
]);

export const LAC_STATUS = reflectKeys(["PROCESSING", "READY", "ERROR"]);
export type LacStatusType = keyof typeof LAC_STATUS;

export const LAC_ERROR_REASON = reflectKeys(["RESOURCE_NOT_FOUND"]);
export type LacErrorReason = keyof typeof LAC_ERROR_REASON;

export const ACTIVITY_TYPE = {
  SECTION: "SECTION",
  ACTIVITY: "ACTIVITY",
  ACTIVITY_QUIZ: "ACTIVITY_QUIZ",
} as const;
export type ActivityType = keyof typeof ACTIVITY_TYPE;

export const ACTIVITY_LEARNING_TYPE = reflectKeys([
  "CUSTOM",
  "ACQUISITION",
  "COLLABORATION",
  "DISCUSSION",
  "INQUIRY",
  "PRACTICE",
  "PRODUCTION",
]);
export type ActivityLearningType = keyof typeof ACTIVITY_LEARNING_TYPE;

export const ACTIVITY_LEARNING_MODE_TYPE = reflectKeys(["STANDARD", "DIAGNOSTIC"]);

export type LacErrorType =
  | "XML_NOT_FOUND"
  | "XML_INVALID"
  | "QTI_VERSION_MISMATCH"
  | "MISSING_INTERACTION"
  | "MISSING_RESPONSE_DECLARATION"
  | "MISSING_ITEM_BODY"
  | "MULTI_INTERACTION_DETECTED"
  | "UNSUPPORTED_QUESTION_TYPE"
  | "SEND_SQS_MESSAGE_FAILED"
  | "QUESTION_CREATE_FAILED"
  | "QUESTION_VALIDATION_FAIL"
  | "RESOURCE_NOT_FOUND";

export const LESSON_TYPE = reflectKeys(["NEW", "COURSE", "HTML5", "URL"]);
export type LessonType = (typeof LESSON_TYPE)[keyof typeof LESSON_TYPE];

export const LESSON_ACTIVITY_TYPE = reflectKeys(["ACTIVITY", "SECTION", "ACTIVITY_QUIZ"]);

export const ZIP_FILE_TYPE = reflectKeys([
  "ASSIGNMENT_RESPONSE",
  "ASSIGNMENT_MARKS",
  "LEARNING_PROGRESS_ASSIGNMENT",
  "LEARNING_PROGRESS_SELF_STUDY",
  "LEARNING_PROGRESS_ALS",
]);

export interface Lesson {
  accessibleByAssociatedStudent: boolean;
  accessibleByNonAssociatedStudent: boolean;
  accessibleByTeacher: boolean;
  ancestorUuid: string;
  description: string;
  dictionary: string | null;
  duplicatable: boolean;
  gameEnabled: boolean;
  hideSectionActivityNumber: boolean;
  id: string;
  keywords: string;
  language: string;
  pages: LessonActivity[];
  passcodeEnabled: boolean;
  printable: boolean;
  printableAnswers: boolean;
  reattemptable: boolean;
  resourceDomain: string;
  showCover: boolean;
  showSectionCover: boolean;
  status: string;
  thumbnailFileName: string | null;
  thumbnailAcpGenerated: boolean;
  title: string;
  uuid: string;
}

export interface LessonActivityMetadata {
  title: string;
  thumbnailFileName: string | null;
  thumbnailAcpGenerated: boolean;
  description: string;
  pageNo: number;
}

export interface BaseLessonActivity extends LessonActivityMetadata {
  id: string;
  learningType: ActivityLearningType;
  notes: string;
  optional: boolean;
  parentId: number;
  recommendedTime: number;
  type: ActivityType;
  uuid: string;
  modifiedByName?: string;
  modifiedDate?: string;
}

export interface LessonActivity extends BaseLessonActivity {
  alpStageId: number;
  layout: Layout;
}

export interface LessonActivityComponent {
  type: LacType;
  anchor: string;
  containerId: string;
  componentId: string;
  status: string;
  columns: Column[];
  uuid: string;
}

export interface Column {
  componentId?: string;
  status: string;
  items: Component[];
}

export interface Layout {
  rows: LessonActivityComponent[];
}

export interface Component {
  containerId: string;
  componentId: string;
  activityId: string;
  pageId: string;
  type: ComponentType;
  anchor: string;
  text?: string;
  title?: string;
  status: string;
  items?: Component[];
  columns?: Column[];
  uuid: string;
}

export interface OptionOrientation {
  xCoordinate: string;
  yCoordinate: string;
}

export interface Resource {
  id: number;
  uuid: string;
  title: string;
  description: string;
  thumbnailUrl: string | null;
  thumbnailFileName: string | null;
  lessonActivities: LessonActivity[];
  pages?: LessonActivity[];
  resourceDomain: string;
  learningResourceType: string;
  passcodeEnabled: boolean;
  notes: string;
  keywords: string | null;
  status: string;
  language: string;
  dictionary: string | null;
  ancestorUuid: string;
  duplicatable: boolean;
  reattemptable: boolean;
  printable: boolean;
  printableAnswers: boolean;
  accessibleByAssociatedStudent: boolean;
  accessibleByNonAssociatedStudent: boolean;
  accessibleByTeacher: boolean;
  hideSectionActivityNumber: boolean;
  showCover: boolean;
  showSectionCover: boolean;
  gameEnabled: boolean;
}

export interface SectionInfo {
  hideSectionActivityNumber: boolean;
  hideSectionCover: boolean;
  sectionDisplayModel: SectionDisplay;
  newSection: boolean;
}

export interface SectionDisplay {
  id: number;
  title: string;
  pageNo: number;
  startDateTime: string;
  endDateTime: string;
  numCompleted?: number;
  status?: string;
}

export interface Rule {
  id: number;
  lessonId: number;
  conditionTargetId: number;
  conditionTargetType: string;
  type: string;
  startDateTime: string;
  endDateTime: string;
  activityAccessible: boolean;
  groupwork: boolean;
  restriction: string | null;
  restrictionSettings: string | null;
  previousCompletionPrerequisite: boolean;
  resourceCompletionPrerequisite: string | null;
  minPercentagePrerequisite: number;
  maxPercentagePrerequisite: number;
  acceptableResponse: boolean;
}
export interface ResourceTaggings {
  hiddenResourceTaggingContentMapList: ResourceTaggingContentMap[];
  resourceTaggingContentMapList: ResourceTaggingContentMap[];
  resourceTaggingSubjectLevelList: ResourceTaggingSubjectLevel[];
}

export interface ResourceTaggingContentMap {
  contentMapMetadata: ContentMapMetadata;
  taggings: Tagging[];
  tagId: number;
}

export interface ResourceTaggingSubjectLevel {
  levelAndStream: LevelAndStream;
  subject: Subject;
}
// TODO: to unify and regression test. also exists in types/lesson.ts
export interface ContentMapMetadata {
  id: number;
  uuid: string;
  title: string;
  syllabus: string;
  implStartDate: string;
  implEndDate: string;
  ownerGroupId: number;
  viewableByTeachers: boolean;
  als: boolean;
  started: boolean;
  expired: boolean;
}

export interface Tagging {
  id: number | null;
  nodeId: number;
  learningOutcomeId: number;
  text: string;
  name?: string; // for learning outcome display name
  cognitiveLevelId: number | null;
  cognitiveLevelText: string | null;
  path: string;
}

export interface Subject {
  id: number;
  uuid: string | null;
  subjectName: string;
  fromSls: boolean;
  subjectKey: string;
  category: SubjectCategory | null;
  schoolId: number | null;
  remark: string | null;
  schoolName: string | null;
  schoolCode: string | null;
  deprecated?: boolean;
}

export interface LevelAndStream {
  id: number;
  displayName: string;
  levelCode: number | null;
  streamCode: number | null;
  sortGrouping: number;
}

export interface QuizMetadata {
  id: string;
  title: string;
  optional?: boolean;
  pageNo?: number;
  description?: string;
  instruction?: string;
  testMode?: QuizTestMode;
  maxScore?: number;
  totalScore?: number;
  originalTotalScore?: number;
  minCompleteScorePercent?: number;
  retryable?: boolean;
  hideQuizMarks?: boolean;
  totalRecommendedTime?: number;
  timedQuiz?: boolean;
  randomPage?: boolean;
  randomByPageCount?: boolean;
  hideQuestionNumber?: boolean;
  uuid: string;
  gradingRubricUuid: string;
}

export interface Quiz {
  id: number;
  uuid: string;
  description: string | null;
  keywords: string | null;
  indexNo: number;
  marks: number;
  noOfAttempt: number;
  questionType: QuestionType;
  recommendedTime: number;
  createdDate: string;
  modifiedDate: string;
  modifiedBy: {
    id: number;
    name: string;
    displayName: string | null;
  };
  questionBody: string;
  numOfHint: number;
  algebrakitSpec: string | null;
  algebrakitRefId: string | null;
  children: {
    uuid: string;
    indexNo: number;
    questionType: QuestionType;
  }[];
  questionOptions: QuestionOption[];
}

export interface AncestorLesson {
  uuid: string;
  ancestorTitle: string;
  ancestorDomain: ResourceDomain;
  ancestorOwnerGroupId: number | null;
}

export interface LessonActivityBasicInfo {
  id: number;
  title: string;
}

export interface AssignmentTeam {
  id: number;
  uuid: string;
  name: string;
  resourceId: number;
  students: AssignmentTeamMember[];
  tempTeamUuid: string;
  assignmentId: number;
}

interface AssignmentTeamMember {
  userId: number;
  studentInfoId: number;
  status: string;
}

export const RESOURCE_TYPE = reflectKeys(["LESSON", "QUESTION", "POLL", "GRADING_RUBRIC"]);

export interface MoeRelatedVersion {
  uuid: string;
  title: string;
  modifiedDate: string;
  status: string;
}
